import {
	type Channel,
	type ProductVariantAvailability,
} from '@commercetools/platform-sdk'
import { useFetcher, useLoaderData } from '@remix-run/react'
import { useState, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import SearchInput from '#app/components/common/forms/search-input'
import PlentyBody from '#app/components/typography/plenty-body'
import PlentyHeading from '#app/components/typography/plenty-heading'
import { Button } from '#app/components/ui/button'
import {
	DrawerTrigger,
	DrawerContent,
	DrawerFooter,
	Drawer,
} from '#app/components/ui/drawer'
import { municipalitiesDK } from '#app/constants/municipalities-dk'
import useAmplitudeContext from '#app/hooks/use-amplitude-context'
import { type loader as pdpLoader } from '#app/routes/_public+/product+/$styleNumber'
import { type loader } from '#app/routes/_public+/products+/$slug'
import { cn } from '#app/utils/misc'
import StoreListForm from './store-list-form'
import { groupByMunicipality } from './store-selector-sheet'

function getInitialState(selectedStores: any[]) {
	return selectedStores.length > 0 ? selectedStores : []
}
/**
 * Filters the storeChannels array to include only those channels that
 * have at least one variant on stock in the given productInfo.
 *
 * @param storeChannels - The list of store channels.
 * @param productInfo   - The product info, including variant availability.
 * @returns An array of StoreChannel items that are in stock for at least one variant.
 */
export function filterChannelsOnAvailability(
	storeChannels: any[],
	productInfo: any,
): any[] {
	if (!productInfo) return storeChannels

	// Use a Set to collect all channel IDs where any variant is on stock
	const inStockChannelIds = new Set<string>()

	// Combine masterVariant + all additional variants
	const allVariants = [productInfo.masterVariant, ...productInfo.variants]

	for (const variant of allVariants) {
		const channels = variant.availability?.channels
		if (!channels) continue

		// Each entry in the channels object: key=channelId, value=ProductVariantChannel
		for (const [channelId, channelAvailability] of Object.entries(channels)) {
			try {
				if (
					(channelAvailability as ProductVariantAvailability).isOnStock &&
					(channelAvailability as ProductVariantAvailability)
						.availableQuantity! > 1
				) {
					inStockChannelIds.add(channelId)
				}
			} catch (e) {
				console.error(
					`Error checking availability for variant ${variant.id} in channel ${channelId}`,
					e,
				)
			}
		}
	}

	// Filter storeChannels whose ids match an in-stock channel ID
	return storeChannels.filter(channel => inStockChannelIds.has(channel.id))
}
export const StoreSelectorDrawer = ({
	selectedStores,
}: {
	selectedStores: any[]
}) => {
	const [query, setQuery] = useState('')
	const [open, setOpen] = useState(false)
	const [selectedStoresState, setSelectedStores] = useState(
		getInitialState(selectedStores),
	)
	const { trackAmplitudeEvent } = useAmplitudeContext()

	let { t } = useTranslation('product_list_page')

	const addStoresFetcher = useFetcher()

	const data = useLoaderData<typeof loader | typeof pdpLoader>()
	const storesChannels = !data.productData
		? (data.storesChannels as Channel[])
		: (filterChannelsOnAvailability(
				data.storesChannels,
				data.productData.productInfo,
		  ) as Channel[])

	// Perform fuzzy search using useMemo to memoize results
	const filteredChannels = useMemo(() => {
		if (query.trim() === '') {
			return storesChannels // If query is empty, return all channels
		}

		const result = storesChannels.filter(channel => {
			// Normalize query for case-insensitive search
			const normalizedQuery = query.toLowerCase()
			if (normalizedQuery.length === 4 && !isNaN(Number(normalizedQuery))) {
				// Check if the query is a postal code
				return channel.address?.postalCode?.includes(normalizedQuery)
			}
			// Check if the channel name, postal code, or municipality matches the query
			const nameMatches = channel.name?.en
				?.toLowerCase()
				.includes(normalizedQuery)
			const postalCodeMatches = channel.address?.postalCode
				?.toLowerCase()
				.includes(normalizedQuery)

			const municipalitiesMatch = municipalitiesDK
				.filter(municipality =>
					municipality.name.toLowerCase().includes(normalizedQuery),
				)
				.find(municipality =>
					municipality.postalCodes.includes(channel.address?.postalCode ?? ''),
				)

			return nameMatches || postalCodeMatches || municipalitiesMatch
		})

		return result
	}, [query, storesChannels])

	const municipalities = useMemo(
		() => groupByMunicipality(filteredChannels),
		[filteredChannels],
	)
	// const locations = useMemo(
	// 	() => getLocations(filteredChannels),
	// 	[filteredChannels],
	// )

	const handleSelectStores = () => {
		const formData = new FormData()
		formData.append('selectedStores', JSON.stringify(selectedStoresState))
		trackAmplitudeEvent('select stores', {
			selectedStores: selectedStoresState,
		})
		addStoresFetcher.submit(formData, {
			method: 'POST',
			action: '/resources/addSelectedStores',
		})
		setOpen(false)
	}

	// const handleMarkerClick = (store: any, selectedStoresKey: string[]) => {
	// 	const markerStore = filteredChannels.find(st => st.key === store)
	// 	if (markerStore && !selectedStoresKey.includes(markerStore.key)) {
	// 		setSelectedStores(prevSelectedStores => [
	// 			...prevSelectedStores,
	// 			{
	// 				name: markerStore.name?.en,
	// 				key: markerStore.key,
	// 			},
	// 		])
	// 	}
	// }

	const handleSearch = (e: any) => {
		setQuery(e.target.value)
	}

	useEffect(() => {
		if (open) {
			document.body.classList.add('no-scroll')
		} else {
			document.body.classList.remove('no-scroll')
		}

		return () => {
			document.body.classList.remove('no-scroll')
		}
	}, [open])

	return (
		<Drawer open={open} onOpenChange={setOpen}>
			<DrawerTrigger
				className={cn(
					'flex flex-row items-center justify-end gap-x-1 md:justify-normal',
					selectedStores.length > 0 ? 'justify-start' : 'justify-end',
				)}
				onClickCapture={() =>
					trackAmplitudeEvent('open select stores drawerDrawer')
				}
			>
				<PlentyBody className="underline">
					{selectedStores.length > 0
						? t('store_selector_sheet.selected_store_trigger')
						: t('store_selector_sheet.no_store_trigger')}
				</PlentyBody>
			</DrawerTrigger>
			<DrawerContent className="flex flex-col gap-y-6">
				<div className="flex flex-col gap-y-2">
					<PlentyHeading as="h4">
						{selectedStores.length > 0
							? t('store_selector_sheet.heading_selected_store')
							: t('store_selector_sheet.heading_no_store')}
					</PlentyHeading>
					<div className="pt-2">
						<SearchInput
							placeholder={t('store_selector_sheet.search_placeholder')}
							onChange={handleSearch}
						/>
					</div>
				</div>
				{/* Removed from mobile for now cause its ugly and not usefull */}
				{/* <GoogleMapStoresV2Component
					locations={locations}
					zoom={6}
					onMarkerClick={handleMarkerClick}
					selectedStoreChannelKey={selectedStoresState.map(store => store.key)}
					height={'600px'}
				/> */}
				<div
					className="flex w-full grow overflow-y-auto "
					style={{ marginTop: 0 }}
				>
					<StoreListForm
						municipalities={municipalities}
						selectedStores={selectedStoresState}
						setSelectedStores={setSelectedStores}
					/>
				</div>
				<DrawerFooter className="flex  flex-col justify-end">
					<Button
						variant="primary"
						size="primary"
						onClick={handleSelectStores}
						disabled={selectedStoresState.length === 0}
						fullWidth
					>
						{t('store_selector_sheet.show_button')}
					</Button>
				</DrawerFooter>
			</DrawerContent>
		</Drawer>
	)
}

/* Helper functions */
